/**
 * The published cost ranges the LP cost components draw from — ONE copy.
 *
 * Canonical source: docs/seo/LEAD-COST-ECONOMICS-SOURCE-OF-TRUTH.md. Before this
 * file, LPCostFlip and LPRoi each carried their own copy of these numbers, so a
 * correction had to be made twice and could drift on the most sensitive figures
 * on the site. Change a range HERE (after changing the source-of-truth doc) and
 * both components follow.
 *
 *   cpl   cost per LEAD   [low, high]
 *   cpfl  cost per FUNDED LOAN  [low, high | null]; null = open-ended ("$2,000+"),
 *         which is never given an invented ceiling in a calculation.
 *   *Label  the exact published text — it must match the page's cost table.
 *
 * Zillow is deliberately absent: its published range is "varies".
 * The page's own LPCostTable rows are written in the page's MDX (content, not
 * component); the content gates check them against the forbidden-figure list.
 */
export type CostKey = 'shared' | 'rate' | 'vendor' | 'ours';

export interface CostRange {
  cpl: [number, number];
  cplLabel: string;
  cpfl: [number, number | null];
  cpflLabel: string;
}

export const COST: Record<CostKey, CostRange> = {
  // LendingTree-type marketplace, up to 5 buyers
  shared: { cpl: [10, 100], cplLabel: '$10–$100', cpfl: [5000, 10000], cpflLabel: '$5,000–$10,000+' },
  // Bankrate-type rate table, fewer buyers
  rate: { cpl: [100, 250], cplLabel: '$100–$250', cpfl: [2500, 5000], cpflLabel: '$2,500–$5,000' },
  // vendor "exclusive", sold to one buyer
  vendor: { cpl: [50, 150], cplLabel: '$50–$150+', cpfl: [2000, null], cpflLabel: '$2,000+' },
  // first-party exclusive, purchase — the "generate your own" row
  ours: { cpl: [30, 60], cplLabel: '$30–$60', cpfl: [1200, 2000], cpflLabel: '$1,200–$2,000' },
};

/** An open-ended label ends in "+". Charts draw those to a fading edge instead of a made-up ceiling. */
export const isOpenEnded = (r: CostRange) => r.cplLabel.endsWith('+') || r.cpflLabel.endsWith('+');
